import { Component } from '@angular/core';
import { TrainingItemConstants } from '../constants/training-item-constants';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TrainingItemGroup } from '../models/training-item-group';
import { TrainingItemPersistenceService } from '../services/training-item-persistence.service';
import { of, retry, switchMap, timer } from 'rxjs';
import { MessageService } from 'primeng/api';
import { TrainingItemGroupService } from '../services/training-item-group.service';
import { DocumentSearchResult } from '../../document/models/documentSearchResult';
import { CourseCatalogueSearchResult } from '../../course-catalogue/models/course-catalogue-search-result';
import { cloneDeep } from 'lodash-es';
import { TrainingItem } from '../models/training-item';
import { NameValue } from '../../shared/models/name-value';
import { ILinkedTrainingItemDetails } from '../models/linked-training-item-details';
import { ITrainingItemGroupDetailsView } from '../models/training-item-group-details-view';

@Component({
  selector: 'app-training-item-group-general-details',
  templateUrl: './training-item-group-general-details.component.html',
  styleUrls: ['./training-item-group-general-details.component.scss'],
})
export class TrainingItemGroupGeneralDetailsComponent {
  errorMessages: string[] = [];
  isTrainingItemSaveInProgress = false;
  trainingItemSaveProgressMessage =
    TrainingItemConstants.Training_item_Save_InProgress;
  errorMessageHeaderText: string =
    TrainingItemConstants.Training_Item_Creation_Error_Message_Header_Text;
  mandatoryFields: { [key: string]: boolean } =
    TrainingItemConstants.Training_Item_group_Default_Requiredfields;

  displayDataCollected: string[] = [];
  isAddTrainingItemsButtonVisible = false;
  isAddCourseItemsButtonVisible = false;
  trainingItemsDataToDisplay: ILinkedTrainingItemDetails[] = [];
  trainingItemToDelete: any;
  showDeleteTrainingItemsConfirmationModal = false;
  showCheckBoxes: boolean = true;
  selectedCourses: CourseCatalogueSearchResult[] = [];
  isEqmsScopesSelected = false;
  documentNumberListScopeMismatch: string[] = [];
  trainingItemsDeleteMessage = TrainingItemConstants.Training_Item_Delete_Message;

  trainingItemGroupForm!: FormGroup;
  eQMSScopeList: NameValue[] = [];
  scopeList: any = [];
  valueField: string = 'value';
  labelField: string = 'name';
  trainingItemGroup: TrainingItemGroup = new TrainingItemGroup();
  trainingItemGroupToEdit: ITrainingItemGroupDetailsView | null = null;
  isEditTrainingItemGroup: boolean = false;
  trainingItemGroupDetails : string = TrainingItemConstants.TrainingItemGroupDetails;
  trainingItemGroupId!: string;

  constructor(private formBuilder: FormBuilder,
    private trainingItemPersistenceService: TrainingItemPersistenceService,
    private messageService: MessageService,
    private trainingItemGroupService: TrainingItemGroupService
  ) {
  }

  ngOnInit(): void {

    this.trainingItemPersistenceService.closeAndResetTrainingItemSlider$.subscribe((isClose) => {
      if (isClose) { this.closeAndResetTrainingItemSlider(); }
    });
    this.trainingItemGroupForm = this.createTrainingItemForm();
    this.isEditTrainingItemGroup = this.trainingItemPersistenceService.isEditTrainingItemGroupSlider;
    this.initialize();
  }

  createTrainingItemForm() {
    return this.formBuilder.group({
      name: new FormControl('', [Validators.required]),
      eqmsScopes: new FormControl('', [Validators.required]),
      trainingItems: new FormControl(null, [], []),
    });
  }
  initialize() {
    this.trainingItemGroupService
      .geteQMSScopesForTrainingItemGroup()
      .subscribe((eQMSScope: any) => {
        for (let dataItem of eQMSScope) {
          this.eQMSScopeList.push({
            value: dataItem.eQMSScopeId,
            name: dataItem.eQMSScopeName,
          });
          this.scopeList.push({
            id: dataItem.eQMSScopeId,
            name: dataItem.eQMSScopeName,
            ancestors: dataItem.ancestors
          });
        }
        if (this.isEditTrainingItemGroup) {
          this.setTrainingItemGroupFormControlValues();
        }
      });
  }


  closeAndResetTrainingItemSlider() {
    this.trainingItemGroupForm.reset();
    this.errorMessages = [];
    Object.keys(this.trainingItemGroupForm.controls).forEach((key) => {
      this.mandatoryFields[key] = false;
    });
  }

  saveTrainingItemGroup() {
    this.validateForm();
    if (!this.errorMessages.length) {
      this.getTrainingItemsSelected();
      this.sendTrainingItemGroup();
    }
  }

  getTrainingItemsSelected() {
    this.trainingItemGroupForm.getRawValue();
    this.trainingItemGroup.name = this.trainingItemGroupForm.getRawValue().name;
    this.trainingItemGroup.eqmsScopes = this.trainingItemGroupForm.getRawValue().eqmsScopes;
    this.trainingItemGroup.trainingItems = [];
    this.trainingItemGroup.number = this.isEditTrainingItemGroup
      && this.trainingItemGroupToEdit?.number ? this.trainingItemGroupToEdit?.number : null;

    for (const item of this.trainingItemsDataToDisplay) {
      const trainingItem: TrainingItem = new TrainingItem();
      trainingItem.trainingItemNumber = item.number;
      trainingItem.trainingItemType = item.trainingType;
      trainingItem.scheduled = item.scheduled;
      this.trainingItemGroup.trainingItems.push(trainingItem);
    }
  }



  sendTrainingItemGroup() {
    this.trainingItemSaveProgressMessage = this.isEditTrainingItemGroup ? TrainingItemConstants.Training_item_Update_InProgress
          : TrainingItemConstants.Training_item_Save_InProgress;
    this.isTrainingItemSaveInProgress = true;
    this.trainingItemGroupService
      .saveTrainingItemGroupDetails(this.trainingItemGroup, this.isEditTrainingItemGroup)
      .pipe(        
      switchMap((trainingItemGroup) => {
        this.trainingItemGroupId = trainingItemGroup.id;
        // If not editing, call getTrainingItemGroupById with retry
        if (!this.isEditTrainingItemGroup) {
          return this.trainingItemGroupService.getTrainingItemGroupById(this.trainingItemGroupId).pipe(
            retry({
              count: 5,
              delay: (error, retryCount) => {
                if (retryCount >= 5) {
                  this.messageService.add({
                    severity: TrainingItemConstants.Severity_Err,
                    summary: TrainingItemConstants.Training_Item__Creation_Failed,
                    sticky: true
                  });
                  this.isTrainingItemSaveInProgress = false;
                  this.trainingItemPersistenceService.isTrainingItemGroupUpdated = false;
                  throw error;
                }
                return timer(3000); // Retry delay
              }
            })
          );
        } else {
          // If editing, return the saved trainingItemGroup as observable
          return of(trainingItemGroup); // Import `of` from 'rxjs'
        }
      })
    )
      .subscribe((resp) => {
        const successMessage = this.isEditTrainingItemGroup ? TrainingItemConstants.Training_Item__Updated_Success
          : TrainingItemConstants.Training_Item__Created_Success;
        const successMessageWithNumber = successMessage.replace(/Number/g, resp.number);
        this.messageService.add({
          severity: TrainingItemConstants.Severity_Success,
          summary: successMessageWithNumber
        });
        this.isTrainingItemSaveInProgress = false;
        this.trainingItemPersistenceService.isTrainingItemGroupUpdated = this.isEditTrainingItemGroup;
        this.trainingItemPersistenceService.closeAndResetTrainingItemSlider$.next(
          true
        );
      });
  }

  public validateForm() {
    this.errorMessages = [];
    this.mandatoryFields =
      TrainingItemConstants.Training_Item_group_Default_Requiredfields;
    if (this.trainingItemGroupForm.valid && this.trainingItemsDataToDisplay?.length) {
      return;
    }
    Object.keys(this.trainingItemGroupForm.controls).forEach((key) => {
      if (this.trainingItemGroupForm.get(key)?.errors) {
        this.errorMessages.push(this.getErrorMessageText(key));
      }
      else {
        this.mandatoryFields[key] = false;
      }
    });
    if (!this.trainingItemsDataToDisplay?.length) {
      this.errorMessages.push(TrainingItemConstants.Atleast_One_Training_Item_Must_Be_Selected_Error_Message);
    }
    else {
      this.mandatoryFields['trainingItems'] = false;
    }

    document.querySelector('.formwrap')?.scrollTo(0, 0);
  }

  private getErrorMessageText(labelName: string): string {
    const errorLabelMapper: { [key: string]: string } = {
      name: TrainingItemConstants.Training_Item_Name_Required,
      eqmsScopes: TrainingItemConstants.Training_Item_Scope_Required,
    };
    this.mandatoryFields[labelName] = true;
    return errorLabelMapper[labelName];
  }

  //Below section of code is for the doc search and course search 



  trainingItemsSelectedData(event: DocumentSearchResult[]) {
    let selectedDocumentsList: string[] = [];
    event.forEach(element => {
      selectedDocumentsList.push(element.documentNumber);
    });

    this.displayDataCollected = selectedDocumentsList;
  }


  courseItemsSelectedData = (courses: CourseCatalogueSearchResult[]) => {
    this.selectedCourses = [];
    this.selectedCourses = cloneDeep(courses);
  }

  addDocumentItems(docSearch: any) {
    const data = Object.assign([], this.displayDataCollected);
    this.getTrainingDocumentItems(data);
    docSearch.getLinkedItemsSelection()
  }

  addCourseItems(courseSearch: any) {
    const courseData = Object.assign([], this.selectedCourses);
    const modifiedCourseData = courseData.map((course: any) => ({
      number: course.courseNumber,
      title: course.name,
      ...course,
    }));

    this.getCourseSelectedItems(modifiedCourseData);
    courseSearch.getCourseSelection();
  }

  openDocument(number: string, version: number | null, trainingItemType: number) {
    if (trainingItemType === 1 && version !== null) {
      var url = location.origin.concat("/eqms.documentDetails/", number, ":", version.toString());
      window.open(url, "_blank");
    }
    else if (trainingItemType === 2) {
      var url = location.origin.concat("/training/eqms.courses/", number);
      window.open(url, "_blank");
    }
  }

  //To get the courses selected and to add them to field set
  getCourseSelectedItems(selectedCoursesList: any) {
    if (selectedCoursesList.length <= 0) {
      this.messageService.add({
        severity: TrainingItemConstants.Severity_Err,
        summary: TrainingItemConstants.Atleast_One_Training_Item_Must_Be_Selected_Error_Message,
        life: 20000
      });
    } else {
      const matchingDocuments: any[] = [];
      const nonMatchingDocuments: any[] = [];

      selectedCoursesList.forEach((course: any) => {
        const matchingItem = this.trainingItemsDataToDisplay?.find((item: any) => item.number === course.number);
        if (matchingItem) {
          matchingDocuments.push(matchingItem);
        } else {
          nonMatchingDocuments.push(course);
        }
      });

      if (matchingDocuments?.length) {
        const documentNumbersString = matchingDocuments.map((document: any) => ` ${document.number}`).join(', ');
        const validationMessage = TrainingItemConstants.Course_Already_Exists_Error_Message.replace('CourseNumbers', documentNumbersString);
        this.messageService.add({
          severity: TrainingItemConstants.Severity_Err,
          summary: validationMessage,
          life: 20000
        });
      }

      if (nonMatchingDocuments?.length) {
        //get scopes selected from the form
        const eqmsScopes: number[] = Object.assign([], this.trainingItemGroupForm.getRawValue().eqmsScopes);
        this.getParentScopesAndCompare(nonMatchingDocuments, eqmsScopes, 2);
      }
    }
  }
  //To get the documents selected and to add them to field set
  getTrainingDocumentItems(selecteddocumentsList: string[]) {
    if (selecteddocumentsList.length <= 0) {
      this.messageService.add({
        severity: TrainingItemConstants.Severity_Err,
        summary: TrainingItemConstants.Atleast_One_Training_Item_Must_Be_Selected_Error_Message,
        life: 20000
      });
    } else {
      const matchingDocuments: any[] = [];
      const nonMatchingDocuments: any[] = [];

      selecteddocumentsList.forEach((documentNumber: string) => {
        const matchingItem = this.trainingItemsDataToDisplay?.find((item: any) => item.number === documentNumber);
        if (matchingItem) {
          matchingDocuments.push(matchingItem);
        } else {
          nonMatchingDocuments.push(documentNumber);
        }
      });

      if (matchingDocuments?.length) {
        const documentNumbersString = matchingDocuments.map((document: any) => ` ${document.number}`).join(', ');
        const validationMessage = TrainingItemConstants.Document_Already_Exists_Error_Message.replace('DocumentNumbers', documentNumbersString);

        this.messageService.add({
          severity: TrainingItemConstants.Severity_Err,
          summary: validationMessage,
          life: 20000
        });
      }

      if (nonMatchingDocuments?.length) {
        this.trainingItemGroupService
          .getEffectiveOrLatestVersionOfDocuments(nonMatchingDocuments)
          .subscribe((serviceData) => {
            //get sopes selected from the form
            const eqmsScopes: number[] = Object.assign([], this.trainingItemGroupForm.getRawValue().eqmsScopes);
            this.getParentScopesAndCompare(serviceData, eqmsScopes, 1);
          });
      }
    }
  }

  //Display documents in the fieldset
  displayDocuments(serviceData: any[], trainingType: number) {
    let linkedItems = serviceData.map(item => {
      return <ILinkedTrainingItemDetails>{
        title: trainingType === 1 ? item.title : item.name,
        number: item.number,
        version: trainingType === 1 ? item.version : null,
        trainingType: trainingType,
        eqmsScopes: item.eqmsScopes,
        scheduled: item.scheduled
      }
    });
    if (!this.trainingItemsDataToDisplay || this.trainingItemsDataToDisplay.length === 0) {
      this.trainingItemsDataToDisplay = linkedItems;
    } else {
      this.trainingItemsDataToDisplay.push(...linkedItems);
    }
    if (this.trainingItemsDataToDisplay.length > 1) {
      this.trainingItemsDataToDisplay.sort((a: any, b: any) => a.title.localeCompare(b.title));
    }
    document.querySelector('.formwrap')?.scrollTo(0, 0);
  }

  //since scopes are not matching, we are trying to get its parent scopes and compare with selected form scopes
  getParentScopesAndCompare(serviceData: any[], eqmsScopes: number[], trainingType: number) {
    const parentScopes: any[] = eqmsScopes;
    this.scopeList.forEach((eqmsScope: any) => {
      if (eqmsScopes.includes(eqmsScope.id)) {
        eqmsScope.ancestors.forEach((ancestor: any)=>{
          parentScopes.push(ancestor);
        })
      }
    })
    const matchingDocuments: any[] = [];
    const mismatchedDocuments: any[] = [];

    serviceData.forEach((data) => {
      const { eqmsScopes } = data;
      const hasMatch = eqmsScopes.some((scope: number) => parentScopes.includes(scope));
      if (hasMatch) {
        matchingDocuments.push(data);
      } else {
        mismatchedDocuments.push(data);
      }
    });

    if (matchingDocuments.length) {
      this.displayDocuments(matchingDocuments, trainingType);
    }

    if (mismatchedDocuments.length) {
      this.displayScopeValidationMessage(mismatchedDocuments, trainingType);
    }
  }
  //Validation message to display if selected form scopes doesn't match with any of the document's scopes also with its parents scopes
  displayScopeValidationMessage(mismatchedDocuments: any[], trainingType: number) {
    const documentorCoursesNumbersString = mismatchedDocuments.map((document) => `  ${document.number}`).join(', ');
    const validationMessage = trainingType === 1
      ? TrainingItemConstants.Selected_Documents_Not_Matching_Error_Message.replace('DocumentNumbers', `\n${documentorCoursesNumbersString}`)
      : TrainingItemConstants.Selected_Courses_Not_Matching_Error_Message.replace('CourseNumbers', `\n${documentorCoursesNumbersString}`);

    this.messageService.add({
      severity: TrainingItemConstants.Severity_Err,
      summary: validationMessage,
      life: 20000
    });
  }


  //below code is to delete the selected items
  showConfirmationPopupForDeletedAttachments(itemToDelete: any) {
    this.trainingItemToDelete = itemToDelete;
    this.showDeleteTrainingItemsConfirmationModal = true;
  }


  closeConfirmationModal = () => {
    this.showDeleteTrainingItemsConfirmationModal = false;
  }


  updateSelectedData() {
    this.trainingItemsDataToDisplay.forEach((element: ILinkedTrainingItemDetails, index: number) => {
      if (element.number === this.trainingItemToDelete) {
        const indexItemToBeRemoved = index;
        this.trainingItemsDataToDisplay.splice(indexItemToBeRemoved, 1);
      }
    });

    this.showDeleteTrainingItemsConfirmationModal = false;
  }

  onEqmsScopesChange() {
    if (this.trainingItemGroupForm.getRawValue().eqmsScopes == null || this.trainingItemGroupForm.getRawValue().eqmsScopes.length == 0) {
      this.isEqmsScopesSelected = false;
    }
    else
      this.isEqmsScopesSelected = true;
  }

  setTrainingItemGroupFormControlValues = () => {
    this.trainingItemsDataToDisplay = [];
    this.trainingItemGroupToEdit = this.trainingItemPersistenceService.getSelectedTrainingItemGroupForEdit();
    if (this.trainingItemGroupToEdit !== null) {
      this.trainingItemGroupForm.patchValue({
        name: this.trainingItemGroupToEdit?.name,
        eqmsScopes: this.trainingItemGroupToEdit?.eqmsScopes,
      });
      let linkedItems = this.trainingItemGroupToEdit.linkedTrainingItems.map((item: any) => {
        return <ILinkedTrainingItemDetails>{
          title: item.title,
          number: item.number,
          version: item.version,
          trainingType: item.trainingType,
          eqmsScopes: item.eqmsScopes,
          scheduled: item.scheduled
        }
      });
      this.trainingItemsDataToDisplay.push(...linkedItems);
      this.isEqmsScopesSelected = this.trainingItemGroupToEdit?.eqmsScopes.length > 0;
    };
  }

}
